/**
 * Downloads dashboard with FULLY INTERACTIVE BAR CHARTS
 * Creates self-contained HTML with Chart.js bar charts and working buttons
 * @param {Object} allFilterData - Data for all filter combinations
 * @param {string} dashboardName - Name of the dashboard
 * @param {Object} initialFilters - Initial filter state
 * @param {Object} config - Configuration for chart types and layout
 */
export const downloadInteractiveBarChartDashboard = async (allFilterData, dashboardName, initialFilters, config = {}) => {
  try {
    const htmlContent = generateInteractiveBarChartHTML(dashboardName, allFilterData, initialFilters, config);

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${dashboardName.replace(/\s+/g, '_')}_Interactive_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error downloading interactive bar chart dashboard:', error);
    alert('Failed to download dashboard. Please try again.');
  }
};

const generateInteractiveBarChartHTML = (dashboardName, allFilterData, initialFilters, config) => {
  const {
    chartType = 'bar', // 'bar', 'horizontalBar'
    chartTitle = 'Chart',
    dataKey = 'value',
    labelKey = 'label',
    showLegend = true,
    filterGroups = [], // Array of { name: 'year', options: ['2015', '2016', '2017'], label: 'Year' }
    tableDataKey = 'tableData',
    chartDataKey = 'chartData'
  } = config;
  
  // These are used in the template string below, but ESLint doesn't detect that
  // eslint-disable-next-line no-unused-vars
  const _unused = { chartType, chartTitle, dataKey, labelKey, showLegend, filterGroups };

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${dashboardName} Dashboard</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
    <script>
        (function() {
            let attempts = 0;
            const maxAttempts = 50;
            const checkChart = setInterval(function() {
                attempts++;
                if (typeof Chart !== 'undefined') {
                    clearInterval(checkChart);
                    console.log('Chart.js loaded successfully');
                    if (document.readyState !== 'loading' && typeof initializeDashboard === 'function') {
                        setTimeout(initializeDashboard, 100);
                    }
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkChart);
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.umd.min.js';
                    script.onload = function() {
                        console.log('Chart.js loaded from alternative CDN');
                        if (typeof initializeDashboard === 'function') {
                            setTimeout(initializeDashboard, 100);
                        }
                    };
                    document.head.appendChild(script);
                }
            }, 100);
        })();
    </script>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 20px;
            color: #1a202c;
        }
        .container {
            max-width: 1400px;
            margin: 0 auto;
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 32px;
            text-align: center;
        }
        .header h1 { font-size: 32px; font-weight: 700; margin-bottom: 8px; }
        .header .subtitle { font-size: 16px; opacity: 0.95; }
        .export-date { margin-top: 12px; font-size: 14px; opacity: 0.9; }
        .dashboard-content { padding: 32px; }
        .card {
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            padding: 24px;
            margin-bottom: 24px;
        }
        .card-title { font-size: 20px; font-weight: 700; margin-bottom: 20px; color: #2d3748; }
        .controls { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
        .button-group {
            display: flex;
            gap: 8px;
            background: #f7fafc;
            padding: 4px;
            border-radius: 8px;
        }
        .button {
            padding: 8px 20px;
            border: none;
            background: transparent;
            color: #4a5568;
            font-weight: 600;
            font-size: 14px;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
        }
        .button:hover { background: #e2e8f0; }
        .button.active {
            background: #667eea;
            color: white;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }
        .charts-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
            margin-bottom: 24px;
        }
        .chart-container {
            background: #f7fafc;
            padding: 20px;
            border-radius: 12px;
            height: 450px;
            position: relative;
        }
        .chart-canvas-wrapper {
            position: relative;
            height: 380px;
            width: 100%;
        }
        .chart-container canvas { max-height: 100% !important; width: 100% !important; }
        .chart-title { font-size: 14px; font-weight: 600; color: #4a5568; margin-bottom: 12px; }
        .filters-section {
            background: #f7fafc;
            padding: 20px;
            border-radius: 12px;
            margin-bottom: 24px;
        }
        .filters-section h3 { font-size: 16px; font-weight: 600; margin-bottom: 12px; color: #2d3748; }
        .filters-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        .filter-item {
            padding: 12px;
            background: white;
            border-radius: 8px;
            border-left: 4px solid #667eea;
        }
        .filter-item strong { color: #4a5568; margin-right: 8px; }
        .data-section { margin-top: 32px; }
        table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            background: white;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        thead { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; }
        th { padding: 14px; text-align: left; font-weight: 600; font-size: 13px; }
        td { padding: 12px 14px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
        tbody tr:hover { background-color: #f7fafc; }
        .search-box { margin-bottom: 16px; }
        .search-box {
            position: relative;
        }
        .search-box input {
            width: 100%;
            padding: 10px 16px 10px 40px;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-size: 14px;
        }
        .search-box input:focus { outline: none; border-color: #667eea; }
        .search-icon-wrapper {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
            color: #718096;
        }
        .print-button {
            position: fixed;
            bottom: 30px;
            right: 30px;
            padding: 14px 28px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 50px;
            cursor: pointer;
            font-size: 15px;
            font-weight: 600;
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
            transition: all 0.3s;
            z-index: 1000;
        }
        .print-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(102, 126, 234, 0.5);
        }
        @media print {
            body { background: white; padding: 0; }
            .print-button { display: none; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>${dashboardName} Dashboard</h1>
            <div class="subtitle">Fully Interactive Dashboard - All Charts & Data Embedded</div>
            <div class="export-date">Exported: ${new Date().toLocaleString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}</div>
        </div>
        <div class="dashboard-content">
            <div id="dashboard-root"></div>
        </div>
    </div>
    <button class="print-button" onclick="window.print()"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>Print / Save PDF</button>
    <script>
        const ALL_DATA = ${JSON.stringify(allFilterData, null, 2)};
        const INITIAL_FILTERS = ${JSON.stringify(initialFilters, null, 2)};
        const CONFIG = ${JSON.stringify(config, null, 2)};
        const CHART_DATA_KEY = '${chartDataKey}';
        const TABLE_DATA_KEY = '${tableDataKey}';
        
        let currentFilters = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
            currentFilters[key] = INITIAL_FILTERS[key];
        });
        let searchTerm = '';
        let chartInstance = null;
        
        function initializeDashboard() {
            if (typeof Chart === 'undefined') {
                setTimeout(initializeDashboard, 100);
                return;
            }
            renderDashboard();
            setTimeout(function() { createChart(); }, 200);
        }
        
        if (document.readyState === 'loading') {
            window.addEventListener('DOMContentLoaded', initializeDashboard);
        } else {
            initializeDashboard();
        }
        
        function getCurrentData() {
            let data = ALL_DATA;
            for (const key in currentFilters) {
                const value = currentFilters[key];
                if (data && data[value]) {
                    data = data[value];
                } else {
                    const result = {};
                    result[CHART_DATA_KEY] = [];
                    result[TABLE_DATA_KEY] = [];
                    return result;
                }
            }
            return data || (function() {
                const result = {};
                result[CHART_DATA_KEY] = [];
                result[TABLE_DATA_KEY] = [];
                return result;
            })();
        }
        
        function renderDashboard() {
            try {
                const currentData = getCurrentData();
                let filterButtons = '';
                CONFIG.filterGroups.forEach(group => {
                    let buttons = '';
                    group.options.forEach(opt => {
                        const isActive = currentFilters[group.name] === opt ? 'active' : '';
                        buttons += '<button class="button ' + isActive + '" onclick="changeFilter(\\'' + group.name + '\\', \\'' + opt + '\\')">' + opt + '</button>';
                    });
                    filterButtons += '<div class="button-group">' + buttons + '</div>';
                });
                
                let filterDisplay = '';
                CONFIG.filterGroups.forEach(group => {
                    filterDisplay += '<div class="filter-item"><strong>' + group.label + ':</strong> ' + (currentFilters[group.name] || 'N/A') + '</div>';
                });
                
                const html = '<div class="card">' +
                    '<h2 class="card-title">' + (CONFIG.dashboardTitle || '${dashboardName}') + '</h2>' +
                    '<div class="controls">' + filterButtons + '</div>' +
                    '<div class="filters-section">' +
                    '<h3><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>Applied Filters</h3>' +
                    '<div class="filters-grid">' + filterDisplay + '</div>' +
                    '</div>' +
                    '<div class="charts-grid">' +
                    '<div class="chart-container">' +
                    '<h4 class="chart-title">' + CONFIG.chartTitle + '</h4>' +
                    '<div class="chart-canvas-wrapper">' +
                    '<canvas id="mainChart"></canvas>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<div class="data-section">' +
                    '<h2 class="card-title"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>Dashboard Data</h2>' +
                    '<div class="search-box">' +
                    '<div class="search-icon-wrapper">' +
                    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                    '<circle cx="11" cy="11" r="8"></circle>' +
                    '<path d="m21 21-4.35-4.35"></path>' +
                    '</svg>' +
                    '</div>' +
                    '<input type="text" id="searchInput" placeholder="Search data..." oninput="handleSearch(event)" value="' + searchTerm + '">' +
                    '</div>' +
                    '<div id="tableContainer"></div>' +
                    '</div>';
                
                const root = document.getElementById('dashboard-root');
                if (root) {
                    root.innerHTML = html;
                    renderTable();
                }
            } catch (error) {
                console.error('Error rendering dashboard:', error);
            }
        }
        
        function createChart() {
            try {
                if (chartInstance && typeof chartInstance.destroy === 'function') {
                    chartInstance.destroy();
                    chartInstance = null;
                }
            } catch (e) {
                console.warn('Error destroying chart:', e);
            }
            
            setTimeout(() => {
                if (typeof Chart === 'undefined') return;
                
                const currentData = getCurrentData();
                const chartData = currentData[CHART_DATA_KEY] || [];
                const ctx = document.getElementById('mainChart');
                if (!ctx || !chartData.length) return;
                
                const isHorizontal = CONFIG.chartType === 'horizontalBar';
                const labels = chartData.map(d => d[CONFIG.labelKey] || d.label || d.name || d.period || '');
                const datasets = [];
                
                if (chartData[0] && typeof chartData[0] === 'object') {
                    const firstKey = Object.keys(chartData[0]).find(k => k !== CONFIG.labelKey && k !== 'label' && k !== 'name' && k !== 'period');
                    if (firstKey && (chartData[0].currentYTD !== undefined || chartData[0].priorYTD !== undefined || chartData[0].sales !== undefined)) {
                        if (chartData[0].currentYTD !== undefined) {
                            datasets.push({
                                label: 'Current YTD',
                                data: chartData.map(d => d.currentYTD || 0),
                                backgroundColor: '#3B82F6',
                                borderColor: '#3B82F6',
                                borderWidth: 1
                            });
                        }
                        if (chartData[0].priorYTD !== undefined) {
                            datasets.push({
                                label: 'Prior YTD',
                                data: chartData.map(d => d.priorYTD || 0),
                                backgroundColor: '#FBBF24',
                                borderColor: '#FBBF24',
                                borderWidth: 1
                            });
                        }
                        if (chartData[0].sales !== undefined && chartData[0].priorYTD === undefined) {
                            datasets.push({
                                label: 'Sales',
                                data: chartData.map(d => d.sales || 0),
                                backgroundColor: CONFIG.colors?.[0] || '#10B981',
                                borderColor: CONFIG.colors?.[0] || '#10B981',
                                borderWidth: 1
                            });
                        }
                    } else {
                        const dataKey = CONFIG.dataKey || 'value' || 'sales';
                        datasets.push({
                            label: CONFIG.dataKeyLabel || 'Value',
                            data: chartData.map(d => d[dataKey] || d.value || d.sales || 0),
                            backgroundColor: CONFIG.colors?.[0] || '#3B82F6',
                            borderColor: CONFIG.colors?.[0] || '#3B82F6',
                            borderWidth: 1
                        });
                    }
                }
                
                chartInstance = new Chart(ctx, {
                    type: 'bar',
                    data: { labels, datasets },
                    options: {
                        indexAxis: isHorizontal ? 'y' : 'x',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: CONFIG.showLegend !== false, position: 'top' },
                            tooltip: { enabled: true }
                        },
                        scales: {
                            [isHorizontal ? 'x' : 'y']: {
                                beginAtZero: true,
                                title: { display: true, text: CONFIG.axisLabel || 'Value' }
                            }
                        }
                    }
                });
            }, 100);
        }
        
        function renderTable() {
            const container = document.getElementById('tableContainer');
            if (!container) return;
            
            const currentData = getCurrentData();
            let data = currentData[TABLE_DATA_KEY] || [];
            
            if (!Array.isArray(data) || data.length === 0) {
                container.innerHTML = '<p style="color: #718096; padding: 20px; text-align: center;">No data available</p>';
                return;
            }
            
            const filteredData = data.filter(row => {
                if (!searchTerm) return true;
                return Object.values(row).some(val => 
                    String(val || '').toLowerCase().includes(searchTerm.toLowerCase())
                );
            });
            
            if (filteredData.length === 0) {
                container.innerHTML = '<p style="color: #718096; padding: 20px; text-align: center;">No matching data found</p>';
                return;
            }
            
            const headers = Object.keys(filteredData[0]);
            let tableHTML = '<table><thead><tr>';
            headers.forEach(header => {
                tableHTML += \`<th>\${formatLabel(header)}</th>\`;
            });
            tableHTML += '</tr></thead><tbody>';
            
            filteredData.forEach(row => {
                tableHTML += '<tr>';
                headers.forEach(header => {
                    tableHTML += \`<td>\${formatValue(row[header])}</td>\`;
                });
                tableHTML += '</tr>';
            });
            
            tableHTML += '</tbody></table>';
            container.innerHTML = tableHTML;
        }
        
        function changeFilter(filterName, value) {
            currentFilters[filterName] = value;
            renderDashboard();
            setTimeout(() => { createChart(); }, 100);
        }
        
        function handleSearch(event) {
            searchTerm = event.target.value;
            renderTable();
        }
        
        function formatLabel(str) {
            return str.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
        }
        
        function formatValue(val) {
            if (val === null || val === undefined) return '-';
            if (typeof val === 'number') {
                return val.toLocaleString('en-US', { maximumFractionDigits: 2 });
            }
            return String(val);
        }
        
        window.addEventListener('error', function(e) {
            console.error('Global error:', e.error);
        });
    </script>
</body>
</html>`;
};

